import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Autocomplete from "@/components/ui/autocomplete";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { ValueSetBase } from "@/types/valueSet/valueSet";
import valueSetApi from "@/types/valueSet/valueSetApi";
import query from "@/Utils/request/query";
import { mergeAutocompleteOptions } from "@/Utils/utils";

interface ValueSetPreviewProps {
  valueset: ValueSetBase;
  trigger: React.ReactNode;
}

export function ValueSetPreview({ valueset, trigger }: ValueSetPreviewProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");

  const { data: searchQuery, isFetching } = useQuery({
    queryKey: ["valueset", "previewSearch", search, valueset.compose],
    queryFn: query.debounced(valueSetApi.previewSearch, {
      queryParams: { search, count: 20 },
      body: {
        ...valueset,
        name: valueset.name || "Preview",
        slug: valueset.slug || "preview-slug",
        compose: valueset.compose.include[0]?.system
          ? valueset.compose
          : {
              include: [{ system: "http://snomed.info/sct" }],
              exclude: [],
            },
      },
    }),
    enabled: open,
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg pr-2 pl-3">
        <SheetHeader className="space-y-1 px-1">
          <SheetTitle className="text-xl font-semibold">
            {t("valueset_preview")}
          </SheetTitle>
          <SheetDescription>
            {t("valueset_preview_description")}
          </SheetDescription>
        </SheetHeader>
        <div className="px-1 mt-6">
          <Autocomplete
            options={mergeAutocompleteOptions(
              searchQuery?.results?.map((option) => ({
                label: option.display || "",
                value: option.code,
              })) ?? [],
            )}
            value={selected}
            onChange={setSelected}
            onSearch={setSearch}
            placeholder={t("search_concept")}
            noOptionsMessage={
              searchQuery && !isFetching
                ? t("no_results_found")
                : t("searching")
            }
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
